import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import { BehaviorSubject, combineLatest, Subscription } from "rxjs";
import { NonNullableFormBuilder, Validators } from "@angular/forms";
import { OrganizationService } from "@onsip/common/services/api/resources/organization/organization.service";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";
import { MatDialog } from "@angular/material/dialog";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { take } from "rxjs/operators";
import { views } from "@onsip/web/app/phone/views";

enum DomainType {
  sipDomain,
  privateDomain
}

@Component({
  selector: "onsip-migrate-sip-domain-edit",
  templateUrl: "./migrate-sip-domain-edit.component.html",
  styleUrls: ["./migrate-sip-domain-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MigrateSipDomainEditComponent implements OnInit, OnDestroy {
  @Output() editPanel = new EventEmitter<void>();
  form = this.formBuilder.group({
    domainType: [DomainType.sipDomain],
    sipDomain: "",
    privateDomain: ""
  });
  domainTypeValues = DomainType;
  oldDomain!: string;
  defaultDomain = ".onsip.com";
  sipDomains!: Array<string>;
  loadingStateComplete$ = new BehaviorSubject(true);
  views = views;

  private unsubscriber = new Subscription();

  constructor(
    private formBuilder: NonNullableFormBuilder,
    private organizationService: OrganizationService,
    private userService: UserService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.obtainData();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  onSubmitModal(): void {
    const dialog = this.dialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      maxWidth: "292px",
      disableClose: true,
      autoFocus: false,
      data: {
        title: `${this.translate.instant("ONSIP_I18N.MIGRATE_SIP_DOMAIN")} ?`,
        message: this.translate.instant(
          "ONSIP_I18N.ALL_REGISTERED_DEVICES_WILL_BE_TEMPORARILY_INACTIVE_UNTIL_THEY_ARE_UPDATED_WITH_THE_NEW_DOMAIN_INFORMATION"
        ),
        primaryBtnText: this.translate.instant("ONSIP_I18N.CONFIRM"),
        secondaryBtnText: this.translate.instant("ONSIP_I18N.CANCEL"),
        primaryBtnFlat: true
      }
    });

    this.unsubscriber.add(
      dialog.afterClosed().subscribe(result => {
        if (result.doPrimaryAction) {
          this.onSubmit();
        }
      })
    );
  }

  private obtainData(): void {
    this.organizationService.organizationBrowse();
    this.unsubscriber.add(
      combineLatest({
        users: this.userService.orgState,
        org: this.organizationService.organization
      })
        .pipe(take(1))
        .subscribe(({ users, org }) => {
          this.sipDomains = users.map(user => user.authUsername.replace("_", "-"));
          this.oldDomain = org.domain;
          const domainName = org.domain.slice(0, org.domain.indexOf(this.defaultDomain));
          if (this.oldDomain.includes(this.defaultDomain)) {
            this.form.controls.sipDomain.setValue(domainName);
          } else {
            this.form.patchValue({
              sipDomain: this.sipDomains[0],
              privateDomain: org.domain,
              domainType: DomainType.privateDomain
            });
          }
        })
    );
  }

  onSubmit(): void {
    const domainType = this.form.get("domainType")?.value as DomainType;
    this.validatorsPrivateDomainState(domainType);
    if (this.form.invalid) {
      return;
    }
    this.loadingStateComplete$.next(false);

    this.organizationService
      .organizationMigrate({
        OldDomain: this.oldDomain,
        NewDomain: (domainType === DomainType.sipDomain
          ? `${this.form.get("sipDomain")?.value}${this.defaultDomain}`
          : this.form.get("privateDomain")?.value) as string
      })
      .then(response => {
        this.loadingStateComplete$.next(true);
        if (response.status === "success") {
          this.snackbar.openSnackBar(
            this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
            "success"
          );
          const { domain } = Object.values(response.data)[0];
          this.oldDomain = domain;
          this.editPanel.emit();
        } else {
          this.snackbar.openSnackBar(response.data.message, "error");
          this.organizationService.clearErrors();
          return;
        }
      });
  }

  private validatorsPrivateDomainState(domainType: DomainType): void {
    if (domainType === DomainType.privateDomain) {
      this.form.get("privateDomain")?.setValidators([Validators.required]);
    } else {
      this.form.get("privateDomain")?.clearValidators();
      this.form.get("privateDomain")?.setValue("");
    }

    this.form.get("privateDomain")?.updateValueAndValidity();
  }

  cancel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj?.doPrimaryAction) {
            this.editPanel.emit();
          }
        })
    );
  }
}
